import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  capturePendingEntryAttribution,
  clearPendingEntryAttribution,
  disableOciApm,
  enableOciApm,
  getAnalyticsConsent,
  getPendingEntryAttribution,
  isProductionAnalyticsHost,
  isPublicAnalyticsRoute,
  saveAnalyticsConsent,
} from "../analytics/ociApm";

export const ANALYTICS_SETTINGS_EVENT = "portfolio:open-analytics-settings";

const AnalyticsConsent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const entryRef = useRef({
    pathname: location.pathname,
    search: location.search,
    referrer: document.referrer,
  });
  const pendingAttributionRef = useRef(getPendingEntryAttribution());
  const isProduction = isProductionAnalyticsHost(window.location.hostname);
  const isPublicRoute = isPublicAnalyticsRoute(location.pathname);
  const [consent, setConsent] = useState(() => getAnalyticsConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useLayoutEffect(() => {
    const attribution = capturePendingEntryAttribution(entryRef.current);
    if (!attribution) return;

    pendingAttributionRef.current = attribution;
    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const openSettings = () => setSettingsOpen(true);
    window.addEventListener(ANALYTICS_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(ANALYTICS_SETTINGS_EVENT, openSettings);
  }, []);

  useEffect(() => {
    if (!isProduction || !isPublicRoute || consent !== "accepted") {
      if (consent === "declined") {
        clearPendingEntryAttribution();
        pendingAttributionRef.current = null;
      }
      disableOciApm();
      return;
    }
    enableOciApm({
      ...entryRef.current,
      attribution: pendingAttributionRef.current,
    });
  }, [consent, isProduction, isPublicRoute, location.pathname, location.search]);

  if (!isProduction || !isPublicRoute || (consent !== "unknown" && !settingsOpen)) {
    return null;
  }

  const choose = (nextConsent) => {
    saveAnalyticsConsent(nextConsent);
    setConsent(nextConsent);
    setSettingsOpen(false);
    if (nextConsent === "declined") {
      clearPendingEntryAttribution();
      pendingAttributionRef.current = null;
      disableOciApm();
    }
  };

  return (
    <section
      aria-label="Analytics preferences"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-w-3xl rounded-[var(--radius-lg)] border border-line bg-card p-5 shadow-[0_18px_60px_rgba(0,0,0,.22)] sm:p-6"
      role="dialog"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-xl">
          <p className="eyebrow mb-2">Privacy · optional analytics</p>
          <h2 className="font-display text-lg font-semibold text-textmain">
            Help improve this portfolio
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted">
            With your permission, OCI Application Performance Monitoring records limited page
            usage, approximate country, device/browser details, performance, errors, and campaign
            tags. It does not record form contents, screen text, login, or dashboard activity.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button className="btn-ghost" onClick={() => choose("declined")} type="button">
            No thanks
          </button>
          <button className="btn-primary" onClick={() => choose("accepted")} type="button">
            Allow analytics
          </button>
          {settingsOpen && consent !== "unknown" && (
            <button className="btn-ghost" onClick={() => setSettingsOpen(false)} type="button">
              Cancel
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default AnalyticsConsent;
